"use client";
import React, { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import NavSearch from "./NavSearch";
import MobileNavMenu from "./MobileNavMenu";
import { getMerchantHref, getPromotionHref, getEventsHref } from "@/constants/hooks";
import { ChevronDown, ArrowRight } from "lucide-react";

interface Props {
  company_id: string;
  mer_slug: string;
  slug_type: string;
  cat_slug: string;
  promotion_slug: string;
  companyLogo: string;
  categories: any[];
  merchants: any[];
  events: any[];
  promotions: any[];
  blog_url?: string | null;
  blog_title?: string;
  company_name?: string;
}

/* ─────────────────────────────────────────
   Dropdown — state-based (no CSS-only hover)
───────────────────────────────────────── */
function Dropdown({
  label, href, items, getHref, iconKey, active, alignRight = false,
}: {
  label: string; href: string; items: any[];
  getHref: (i: any) => string; iconKey?: string;
  active: boolean; alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className="relative h-full flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* trigger */}
      <button
        className={`flex items-center gap-1.5 text-[14px] font-medium select-none transition-colors duration-200 bg-transparent border-0 outline-none cursor-pointer
          ${active || open ? "text-white" : "text-white/70 hover:text-white"}`}
      >
        {label}
        <ChevronDown
          size={12} strokeWidth={2.5}
          className={`transition-all duration-200 mt-px ${open ? "rotate-180 opacity-100" : "opacity-50"}`}
        />
      </button>

      {/* active indicator */}
      {active && (
        <span className="absolute bottom-0 left-0 w-full h-[2px] rounded-full bg-blue-400" />
      )}

      {/* panel */}
      {open && (
        <div className={`absolute top-full pt-2 z-[500] ${alignRight ? "right-0" : "left-1/2 -translate-x-1/2"}`}>
          <div className="w-56 rounded-2xl overflow-hidden bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-slate-100">

            {/* header */}
            <Link
              href={href}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-4 py-3 bg-indigo-950 hover:bg-indigo-800 no-underline transition-colors group/top"
            >
              <span className="text-[11px] font-black uppercase tracking-[0.18em] text-white/80 group-hover/top:text-white transition-colors">
                All {label}
              </span>
              <ArrowRight size={13} className="text-blue-400" />
            </Link>

            {/* items */}
            <div className="max-h-[280px] overflow-y-auto py-2 px-1.5" style={{ scrollbarWidth: "none" }}>
              {items?.slice(0, 14).map((item: any, i: number) => (
                <Link
                  key={i}
                  href={getHref(item)}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-xl no-underline hover:bg-indigo-800/[0.07] group/row transition-colors"
                >
                  {iconKey && item[iconKey] && (
                    <div className="w-7 h-7 rounded-lg bg-zinc-50 border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                      <Image
                        src={item[iconKey]?.startsWith("http") ? item[iconKey] : `/${item[iconKey]}`}
                        alt="" width={18} height={18} className="object-contain"
                      />
                    </div>
                  )}
                  <span className="text-[13px] font-medium text-slate-600 group-hover/row:text-indigo-800 transition-colors line-clamp-1">
                    {item.name || item.merchant_name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Main
───────────────────────────────────────── */
export default function HeaderClient({
  company_id, mer_slug, slug_type, cat_slug,
  promotion_slug, companyLogo, categories,
  merchants, events, promotions, blog_url, blog_title,
  company_name,
}: Props) {
  const pathname = usePathname();
  const at = (p: string) => p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/");

  const navLink = (href: string, label: string, target?: string) => (
    <Link
      href={href}
      target={target}
      className={`relative flex items-center h-full text-[14px] font-medium no-underline transition-colors duration-200 group
        ${at(href) ? "text-white" : "text-white/70 hover:text-white"}`}
    >
      {label}
      <span className={`absolute bottom-0 left-0 h-[2px] rounded-full bg-blue-400 transition-all duration-300
        ${at(href) ? "w-full" : "w-0 group-hover:w-full"}`} />
    </Link>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <div className="flex items-center justify-between h-[62px] gap-6">

        {/* ════ LEFT logo ════ */}
        <div className="hidden lg:flex shrink-0">
          <Link href="/">
            <Image
              src={companyLogo} alt="Logo" width={160} height={52}
              className="h-9 w-auto object-contain brightness-0 invert" priority
            />
          </Link>
        </div>

        {/* ════ CENTER nav ════ */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 h-full">
          {navLink("/", "Home")}

          <Dropdown
            label="Stores" href="/all-stores/A"
            items={merchants} getHref={(m) => getMerchantHref(m, mer_slug, slug_type)}
            iconKey="merchant_logo" active={at("/all-stores")}
          />

          <Dropdown
            label="Categories" href="/category"
            items={categories} getHref={(c) => `/${c.url}`}
            iconKey="category_image" active={at("/category")}
          />

          <Dropdown
            label="Products" href="/all-products"
            items={[{ name: "Branded Products", url: "products" }]}
            getHref={() => "/products"}
            active={at("/products")}
          />
          {blog_url && navLink(blog_url, blog_title || "Blog", "_blank")}
        </nav>

        {/* Mobile logo */}
        <Link href="/" className="lg:hidden">
          <Image
            src={companyLogo} alt="Logo" width={130} height={44}
            className="h-8 w-auto object-contain brightness-0 invert" priority
          />
        </Link>

        {/* ════ RIGHT ════ */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-5 h-full shrink-0">

          {/* Search */}
          <div className="w-56 xl:w-72">
            <NavSearch
              companyId={company_id}
              mer_slug={mer_slug}
              slug_type={slug_type}
              cat_slug={cat_slug}
            />
          </div>

          {/* Events */}
          {events?.length > 0 && (
            <>
              <div className="w-px h-5 bg-white/10 shrink-0" />
              <Dropdown
                label="Events" href="/events"
                items={events} getHref={(e) => getEventsHref(e, slug_type)}
                active={at("/events")} alignRight
              />
            </>
          )}

          {/* Promotions */}
          {promotions?.length > 0 && (
            <>
              <div className="w-px h-5 bg-white/10 shrink-0" />
              <Dropdown
                label="Promotions" href={`/${promotion_slug}`}
                items={promotions} getHref={(p) => getPromotionHref(p, promotion_slug)}
                active={at(`/${promotion_slug}`)} alignRight
              />
            </>
          )}

        </div>

        {/* Mobile menu */}
        <div className="lg:hidden">
          <MobileNavMenu
            company_id={company_id} mer_slug={mer_slug} slug_type={slug_type}
            cat_slug={cat_slug} categories={categories} merchants={merchants}
            events={events} promotions={promotions} promotion_slug={promotion_slug}
            blog_url={blog_url} blog_title={blog_title} companyLogo={companyLogo}
            company_name={company_name}
          />
        </div>

      </div>
    </div>
  );
}
